import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const Privacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-home">← Back to Home</Link>

        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-last-updated">Last updated: September 30, 2026</p>

        <div className="legal-content">
          <p>
            Dangol LLC ("Dangol," "we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our customer-facing mobile application (the "App") and related services (collectively, the "Service").
          </p>

          <h2>1. Introduction</h2>
          <p>
            This policy applies to individual "Customers" who use the Dangol App to earn rewards. If you are a participating "Business" or "Shop," your use of our platform is governed by the separate Dangol Business Terms of Service and Dangol Business Privacy Policy.
          </p>

          <h2>2. Information We Collect</h2>
          <p>We collect the following types of information:</p>

          <p>
            <strong>Information You Provide:</strong> When you create an account, we collect personal information you provide, such as your name, email address, and phone number.
          </p>

          <p>
            <strong>Transactional & Usage Information:</strong> We collect information about how you use the Service. For Customers, this includes your scan history, rewards progress, and redemption history at each participating Business.
          </p>

          <p>
            <strong>Device Information:</strong> We collect a unique token from your mobile device (an "FCM Token") which is required to send you push notifications.
          </p>

          <p>
            <strong>Location Information (Implicit):</strong> We do not use your device's GPS to track your location. We only infer your presence at a Business or Event when your unique QR code is scanned at that Business's kiosk or at an Event check-in.
          </p>

          <p>
            <strong>Event Information:</strong> When you RSVP to, register for, or attend Events hosted by Businesses or Event Coordinators, we collect information such as your RSVP status and your attendance or check-in records.
          </p>

          <p>
            <strong>Payment Information:</strong> For paid Events, payments are processed by our third-party payment processor (Stripe). We do not collect or store your full payment card details. We may receive and retain limited transaction records, such as the amount, status, and a payment reference, to document your order.
          </p>

          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide, operate, and maintain the Dangol rewards service.</li>
            <li>Process scans, track rewards progress, and facilitate redemptions.</li>
            <li>Facilitate your RSVPs, ticket purchases, and attendance at Events.</li>
            <li>Administer raffles, giveaways, and promotions, including notifying and delivering prizes to winners.</li>
            <li>Send you notifications from Businesses and Event Coordinators, in accordance with your preferences.</li>
            <li>Provide Businesses and Event Coordinators with aggregated, non-identifying analytics, insights, and trends about activity at their establishment or Events.</li>
            <li>Improve our services and user experience.</li>
            <li>Prevent fraud and ensure the security of our platform.</li>
            <li>Comply with legal obligations.</li>
          </ul>

          <h2>4. How We Share Your Information</h2>
          <p>We do not sell your personal information. We may share your information in the following situations:</p>

          <p>
            <strong>With Participating Businesses:</strong> We provide Businesses with access to data related to their specific customers. A Business can see a Customer's scan history, rewards progress, and redemption history at their shop only. They also have access to aggregated and anonymized analytics for their dashboard. A Business cannot see a Customer's activity at any other shop.
          </p>

          <p>
            <strong>With Event Coordinators:</strong> When you RSVP to or attend an Event, we do not share your personal contact information (such as your email address or phone number) with the Business or Event Coordinator hosting it, except that if you win a raffle or promotion, we may share the information reasonably necessary to deliver your prize (which may include your name and email address). Event Coordinators otherwise receive only aggregate, non-identifying information and trends about their Events, such as overall attendance and engagement. Your username — which is anonymous by default and may be changed by you — may be visible to other users in social features such as Event RSVP lists and community message boards.
          </p>

          <p>
            <strong>With Service Providers:</strong> We share information with third-party vendors who help us operate the Service, such as our backend provider (Supabase), our push notification delivery service (Firebase Cloud Messaging), and our payment processor (Stripe) for paid Events.
          </p>

          <p>
            <strong>As Required by Law:</strong> We may disclose your information if required to do so by law.
          </p>

          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or method of electronic storage is 100% secure.
          </p>

          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed to provide you with our services. We will also retain and use your information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
          </p>

          <h2>7. Your Privacy Rights and Choices</h2>
          <p>You have control over your personal information. Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access and review the personal information we hold about you.</li>
            <li>Correct any inaccurate information.</li>
            <li>Request the deletion of your personal information. To request that your account and personal data be deleted, please contact us at the email address provided in the "Contact Us" section below.</li>
          </ul>
          <p>
            <strong>Manage Notification Preferences:</strong> You can opt-in or opt-out of receiving notifications from any specific Business or Event Coordinator at any time through the settings in the Customer App.
          </p>

          <h2>8. Children's Privacy</h2>
          <p>
            Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13.
          </p>

          <h2>9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will provide the latest version of this Privacy Policy in the App.
          </p>

          <h2>10. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us at <a href="mailto:support@dangolapp.com">support@dangolapp.com</a>.
          </p>
        </div>

        <div className="legal-footer">
          <Link to="/terms">Terms of Service</Link>
          <span> | </span>
          <Link to="/privacy-partners">Privacy Policy for Partners</Link>
          <span> | </span>
          <Link to="/">Home</Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
